import { useMemo, useState } from 'react';
import { Minus, Plus, Printer, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Switch } from '@/components/ui/Switch';
import { Textarea } from '@/components/ui/Textarea';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/Sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Fact, StoredImage, WorkerChip } from '@/components/production/shared';
import { describeWorkError, formatDuration } from '@/features/workMode/api';
import { useBomComponents } from '@/features/workMode/hooks';
import {
  DEFECT_SEVERITIES,
  type ScrappedPart,
  type WorkDefect,
  type WorkDefectSeverity,
  type WorkDefectStatus,
} from '@/features/workMode/types';
import {
  DEFECT_STATUS_LABEL,
  DEFECT_STATUS_VARIANT,
  SEVERITY_LABEL,
  SEVERITY_VARIANT,
} from '@/features/workMode/labels';
import type { WorkLookups } from '@/features/workMode/useLookups';
import { scrapLogCents, useUpdateScrapLog } from '@/features/scrap/hooks';
import { SCRAP_TAGS, scrapTagLabel, slugifyScrapTag } from '@/features/scrap/tags';
import { formatCurrency, formatDateTime } from '@/lib/formatters';
import { printDocument } from '@/lib/printDocument';
import { cn } from '@/lib/utils';

interface ScrapLogSheetProps {
  log: WorkDefect | null;
  lookups: WorkLookups;
  canManage: boolean;
  onOpenChange: (open: boolean) => void;
  onPhoto: (path: string) => void;
}

function printScrapLog(log: WorkDefect, lookups: WorkLookups) {
  const total = scrapLogCents(log);
  const opened = printDocument({
    title: 'Scrap report',
    number: `SCRAP-${log.id.slice(0, 8).toUpperCase()}`,
    status: DEFECT_STATUS_LABEL[log.status],
    subtitle: formatDateTime(log.created_at),
    sections: [
      {
        title: 'Report details',
        fields: [
          { label: 'Reported by', value: lookups.personName(log.user_id) },
          { label: 'Workstation', value: lookups.stationName(log.workstation_id) },
          {
            label: 'Finished good',
            value: log.item_id
              ? `${lookups.itemSku(log.item_id) ?? ''} ${lookups.itemName(log.item_id) ?? ''}`.trim()
              : null,
          },
          { label: 'Severity', value: SEVERITY_LABEL[log.severity] },
          { label: 'Time on part', value: formatDuration(log.committed_seconds) },
          {
            label: 'Progress',
            value: log.step_number
              ? `Step ${log.step_number}`
              : log.progress_pct != null
                ? `${log.progress_pct}%`
                : null,
          },
          { label: 'Material cost', value: formatCurrency(log.material_cost_cents) },
          { label: 'Labor cost', value: formatCurrency(log.labor_cost_cents) },
          { label: 'Total scrap value', value: formatCurrency(total) },
          { label: 'Needs rework', value: log.needs_rework ? 'Yes' : 'No' },
          {
            label: 'Scrap type',
            value: (log.scrap_tags ?? []).map(scrapTagLabel).join(', '),
          },
        ],
      },
      { title: 'What went wrong', text: log.description },
      ...(log.scrapped_parts?.length
        ? [
            {
              title: 'Components thrown out',
              table: {
                columns: [
                  { label: 'SKU' },
                  { label: 'Component' },
                  { label: 'Qty', align: 'right' as const },
                ],
                rows: log.scrapped_parts.map((part) => [part.sku, part.name, part.qty]),
              },
            },
          ]
        : []),
      ...(log.rework_notes ? [{ title: 'Rework notes', text: log.rework_notes }] : []),
      ...(log.review_notes ? [{ title: 'Review notes', text: log.review_notes }] : []),
    ],
  });
  if (!opened) toast.error('Allow pop-ups to print or save this scrap report as a PDF.');
}

export function ScrapLogSheet({
  log,
  lookups,
  canManage,
  onOpenChange,
  onPhoto,
}: ScrapLogSheetProps) {
  return (
    <Sheet open={!!log} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-xl">
        {log && (
          <ScrapLogDetail
            key={log.id}
            log={log}
            lookups={lookups}
            canManage={canManage}
            onOpenChange={onOpenChange}
            onPhoto={onPhoto}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function ScrapLogDetail({
  log,
  lookups,
  canManage,
  onOpenChange,
  onPhoto,
}: {
  log: WorkDefect;
  lookups: WorkLookups;
  canManage: boolean;
  onOpenChange: (open: boolean) => void;
  onPhoto: (path: string) => void;
}) {
  const save = useUpdateScrapLog();
  const { data: components = [] } = useBomComponents(log.item_id);
  const [description, setDescription] = useState(log.description);
  const [severity, setSeverity] = useState<WorkDefectSeverity>(log.severity);
  const [tags, setTags] = useState<string[]>(log.scrap_tags ?? []);
  const [customTag, setCustomTag] = useState('');
  const [needsRework, setNeedsRework] = useState(log.needs_rework);
  const [reworkNotes, setReworkNotes] = useState(log.rework_notes ?? '');
  const [parts, setParts] = useState<ScrappedPart[]>(log.scrapped_parts ?? []);
  const [extraName, setExtraName] = useState('');
  const [status, setStatus] = useState<WorkDefectStatus>(log.status);
  const [reviewNotes, setReviewNotes] = useState(log.review_notes ?? '');

  const knownIds = useMemo(() => new Set(parts.map((p) => p.item_id).filter(Boolean)), [parts]);
  const total = scrapLogCents({
    material_cost_cents: log.material_cost_cents,
    labor_cost_cents: log.labor_cost_cents,
  });

  const toggleTag = (value: string) => {
    setTags((cur) =>
      cur.includes(value)
        ? cur.filter((t) => t !== value)
        : cur.length >= 8
          ? cur
          : [...cur, value],
    );
  };

  const addCustomTag = () => {
    const slug = slugifyScrapTag(customTag);
    if (!slug) return;
    setTags((cur) => (cur.includes(slug) || cur.length >= 8 ? cur : [...cur, slug]));
    setCustomTag('');
  };

  const setQty = (index: number, qty: number) => {
    setParts((cur) => cur.map((p, i) => (i === index ? { ...p, qty: Math.max(0, qty) } : p)));
  };

  const addBomPart = (itemId: string) => {
    const c = components.find((row) => row.item_id === itemId);
    if (!c || knownIds.has(itemId)) return;
    setParts((cur) => [...cur, { item_id: c.item_id, sku: c.sku, name: c.name, qty: 1 }]);
  };

  const onSave = () => {
    if (description.trim().length === 0) {
      toast.error('The scrap log needs a description.');
      return;
    }
    if (tags.length === 0) {
      toast.error('Tag the type of scrap — pick at least one.');
      return;
    }
    const scrappedParts = parts.filter((p) => p.name.trim() && p.qty > 0);
    save.mutate(
      {
        id: log.id,
        description: description.trim(),
        severity,
        scrapTags: tags,
        needsRework,
        reworkNotes: needsRework ? reworkNotes.trim() || null : null,
        scrappedParts,
        status,
        reviewNotes: reviewNotes.trim() || null,
      },
      {
        onSuccess: () => {
          toast.success('Scrap log updated.');
          onOpenChange(false);
        },
        onError: (e) => toast.error(describeWorkError(e)),
      },
    );
  };

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-2">
          <Trash2 className="h-5 w-5 text-fresh-salmon-dark" />
          Scrap log
        </SheetTitle>
        <SheetDescription>
          {formatDateTime(log.created_at)} ·{' '}
          {lookups.stationName(log.workstation_id) ?? 'No station'}
        </SheetDescription>
      </SheetHeader>
      <SheetBody className="space-y-5">
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant={SEVERITY_VARIANT[log.severity]}>{SEVERITY_LABEL[log.severity]}</Badge>
          <Badge variant={DEFECT_STATUS_VARIANT[log.status]}>
            {DEFECT_STATUS_LABEL[log.status]}
          </Badge>
          {log.needs_rework && <Badge variant="warning-soft">Needs rework</Badge>}
        </div>

        <WorkerChip
          person={lookups.person(log.user_id)}
          sub={log.item_id ? `Building ${lookups.itemName(log.item_id)}` : 'No item on the bench'}
          size={40}
        />

        {(log.photo_paths ?? []).length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {log.photo_paths.map((p) => (
              <StoredImage
                key={p}
                bucket="work-captures"
                path={p}
                alt="Scrap photo"
                className="aspect-square w-full"
                onClick={() => onPhoto(p)}
              />
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Fact label="Time on the part">{formatDuration(log.committed_seconds)}</Fact>
          <Fact label="How far along">
            {log.step_number
              ? `Step ${log.step_number}`
              : log.progress_pct != null
                ? `${log.progress_pct}% through the build`
                : 'Not said'}
          </Fact>
          <Fact label="Material">{formatCurrency(log.material_cost_cents)}</Fact>
          <Fact label="Labor">
            {log.labor_cost_cents == null
              ? 'No pay rate on file'
              : formatCurrency(log.labor_cost_cents)}
          </Fact>
          <Fact label="Scrap value">{formatCurrency(total)}</Fact>
          <Fact label="Finished good">
            {log.item_id
              ? `${lookups.itemSku(log.item_id) ?? ''} ${lookups.itemName(log.item_id) ?? ''}`.trim()
              : '—'}
          </Fact>
        </div>

        {canManage ? (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="scrap-description">What went wrong</Label>
              <Textarea
                id="scrap-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
              />
            </div>

            <div className="space-y-1.5">
              <Label>How bad</Label>
              <Select value={severity} onValueChange={(v) => setSeverity(v as WorkDefectSeverity)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DEFECT_SEVERITIES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Type of scrap</Label>
              <div className="flex flex-wrap gap-1.5">
                {SCRAP_TAGS.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => toggleTag(t.value)}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-xs',
                      tags.includes(t.value)
                        ? 'border-info bg-info-soft/30 text-info'
                        : 'border-border text-muted-foreground hover:bg-secondary',
                    )}
                  >
                    {t.label}
                  </button>
                ))}
                {tags
                  .filter((t) => !SCRAP_TAGS.some((p) => p.value === t))
                  .map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => toggleTag(t)}
                      className="rounded-full border border-info bg-info-soft/30 px-2.5 py-1 text-xs text-info"
                    >
                      {scrapTagLabel(t)} · remove
                    </button>
                  ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={customTag}
                  onChange={(e) => setCustomTag(e.target.value)}
                  placeholder="Add a type"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addCustomTag();
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={!slugifyScrapTag(customTag)}
                  onClick={addCustomTag}
                >
                  Add
                </Button>
              </div>
            </div>

            <div className="space-y-2 rounded-xl border border-border p-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-foreground">Needs rework</p>
                <Switch
                  checked={needsRework}
                  onCheckedChange={setNeedsRework}
                  aria-label="Needs rework"
                />
              </div>
              {needsRework && (
                <Textarea
                  value={reworkNotes}
                  onChange={(e) => setReworkNotes(e.target.value)}
                  rows={2}
                  placeholder="What should be reworked?"
                />
              )}
            </div>

            <div className="space-y-2">
              <Label>Components thrown out</Label>
              <ul className="divide-y divide-border rounded-xl border border-border">
                {parts.length === 0 && (
                  <li className="px-3 py-2 text-sm text-muted-foreground">None recorded.</li>
                )}
                {parts.map((p, i) => (
                  <li
                    key={`${p.item_id ?? p.name}-${i}`}
                    className="flex items-center gap-2 px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">{p.name}</p>
                      {p.sku && <p className="truncate text-xs text-muted-foreground">{p.sku}</p>}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => setQty(i, p.qty - 1)}
                      aria-label={`Fewer ${p.name}`}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <Input
                      value={String(p.qty)}
                      onChange={(e) =>
                        setQty(i, Number(e.target.value.replace(/[^0-9.]/g, '')) || 0)
                      }
                      className="h-9 w-16 text-center tabular-nums"
                      inputMode="decimal"
                      aria-label={`Quantity of ${p.name}`}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => setQty(i, p.qty + 1)}
                      aria-label={`More ${p.name}`}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
              {components.some((c) => !knownIds.has(c.item_id)) && (
                <div className="flex flex-wrap gap-1.5">
                  {components
                    .filter((c) => !knownIds.has(c.item_id))
                    .map((c) => (
                      <Button
                        key={c.item_id}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addBomPart(c.item_id)}
                      >
                        Add {c.name}
                      </Button>
                    ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  value={extraName}
                  onChange={(e) => setExtraName(e.target.value)}
                  placeholder="Other part (name or SKU)"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && extraName.trim()) {
                      e.preventDefault();
                      setParts((cur) => [
                        ...cur,
                        { item_id: null, sku: '', name: extraName.trim(), qty: 1 },
                      ]);
                      setExtraName('');
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={!extraName.trim()}
                  onClick={() => {
                    setParts((cur) => [
                      ...cur,
                      { item_id: null, sku: '', name: extraName.trim(), qty: 1 },
                    ]);
                    setExtraName('');
                  }}
                >
                  Add
                </Button>
              </div>
            </div>

            <div className="space-y-3 rounded-xl border border-border p-4">
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={status} onValueChange={(v) => setStatus(v as WorkDefectStatus)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(DEFECT_STATUS_LABEL) as WorkDefectStatus[]).map((s) => (
                      <SelectItem key={s} value={s}>
                        {DEFECT_STATUS_LABEL[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="scrap-review">Review notes</Label>
                <Textarea
                  id="scrap-review"
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  rows={3}
                  placeholder="Root cause, what changes, who follows up…"
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            <p className="whitespace-pre-line text-sm text-foreground">{log.description}</p>
            <div className="flex flex-wrap gap-1.5">
              {(log.scrap_tags ?? []).length === 0 ? (
                <span className="text-sm text-muted-foreground">No type tagged.</span>
              ) : (
                log.scrap_tags.map((t) => (
                  <Badge key={t} variant="outline">
                    {scrapTagLabel(t)}
                  </Badge>
                ))
              )}
            </div>
            {log.needs_rework && log.rework_notes && (
              <p className="text-sm text-foreground">Rework: {log.rework_notes}</p>
            )}
            <PartList parts={log.scrapped_parts ?? []} />
            {log.review_notes && (
              <Fact label="Review notes">
                <span className="whitespace-pre-line">{log.review_notes}</span>
              </Fact>
            )}
          </>
        )}
      </SheetBody>
      <SheetFooter>
        <Button type="button" variant="outline" onClick={() => printScrapLog(log, lookups)}>
          <Printer className="h-4 w-4" />
          Print / PDF
        </Button>
        <div className="flex flex-1 justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {canManage && (
            <Button onClick={onSave} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save scrap log'}
            </Button>
          )}
        </div>
      </SheetFooter>
    </>
  );
}

function PartList({ parts }: { parts: ScrappedPart[] }) {
  if (parts.length === 0)
    return <p className="text-sm text-muted-foreground">No components recorded as thrown out.</p>;
  return (
    <ul className="divide-y divide-border rounded-xl border border-border">
      {parts.map((p, i) => (
        <li
          key={`${p.sku}-${p.name}-${i}`}
          className="flex items-center justify-between px-3 py-2 text-sm"
        >
          <span className="min-w-0 truncate text-foreground">
            {p.name} {p.sku && <span className="text-muted-foreground">{p.sku}</span>}
          </span>
          <span className="shrink-0 tabular-nums text-foreground">× {p.qty}</span>
        </li>
      ))}
    </ul>
  );
}
